// File: hooks/use-cart-calculations.ts
import { useMemo } from "react";
import type { CartItemData } from "@/components/cart/types";
import {
  groupItemsForCheckout,
  type CheckoutGroupKey,
} from "@/lib/cart/checkout-groups";
import type { ShippingMode } from "@/lib/checkout/derive-shipping-mode";
import { calculateServiceFee } from "@/lib/config";
import { DEFAULT_CURRENCY } from "@/lib/currencies";
import { calculateSellerShippingTotal } from "@/lib/payments/cart-math";

export type SellerCalculation = {
  items: CartItemData[];
  /**
   * Canonical `bySeller` lookup key — the same string this entry is filed
   * under. Carried structurally so no consumer ever re-derives (or worse,
   * `.split(':')`-parses) the key format; that is `buildCheckoutGroupKey`'s
   * job alone. @see lib/cart/checkout-groups.ts
   */
  groupKey: CheckoutGroupKey;
  sellerId: string;
  sellerName: string;
  sellerAvatarUrl: string;
  currencyCode: string;
  /**
   * Homogeneous by construction — a seller with both `legacy` and `live_rate`
   * listings in the cart yields TWO entries here, not one mixed entry. That is
   * what makes `deriveShippingMode`'s mixed branch unreachable and keeps the
   * group compatible with the server's `mixed_shipping_mode` rejection.
   */
  shippingMode: ShippingMode;
  itemCount: number;
  uniqueItemCount: number;
  subtotal: number;
  shippingTotal: number;
  serviceFee: number;
  total: number;
  allShippingMethodsSelected: boolean;
  hasUnavailableItems: boolean;
  paypalMerchantId: string | null;
  paypalCapabilities: string[];
};

export function useCartCalculations(cartItems: CartItemData[], availability?: Record<string, number>) {
  return useMemo(() => {
    const compute = (items: CartItemData[]) => {
      const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
      const uniqueItemCount = items.length;
      const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

      const shippingTotal = calculateSellerShippingTotal(items);

      const serviceFee = subtotal === 0 ? 0 : calculateServiceFee(subtotal + shippingTotal);
      const total = subtotal + shippingTotal + serviceFee;
      // WS4.6: live_rate listings ship via Shippo at checkout (no legacy
      // method to pick), so they trivially satisfy the gate. Without this
      // bypass, allShippingMethodsSelected is false forever for any live_rate
      // item → Pay button stays disabled → buyer can't reach the sub-flow.
      const allShippingMethodsSelected = items.every(
        i => i.shippingMode === "live_rate" || !!i.selectedShippingMethod
      );

      const hasUnavailableItems = items.some(item => {
        const stock = availability?.[item.listingId] ?? item.maxQuantity;
        const isOutOfStock = item.quantity > stock;
        // Rail-aware: a seller is unavailable iff no payment rail accepts
        // checkout. Never gate on paypalMerchantId (single-rail anti-pattern
        // that false-blocks Stripe-only sellers). Mirrors cart.tsx /
        // cart-item.tsx. See p1-rails-aware-pre-checkout-validation.md.
        const isUnavailable = (item.availableProviders?.length ?? 0) === 0;
        // WS4.6 follow-up: live_rate items legitimately ship via Shippo with no
        // fixed shipping_config row. Mirror the bypass already in
        // `allShippingMethodsSelected` above, otherwise live_rate items get
        // flagged unavailable and the Pay button stays disabled.
        const hasNoShippingMethods = item.shippingMode !== "live_rate" && (item.shippingMethods?.length ?? 0) === 0;
        const isCancelled = Boolean((item as any).chatPrivateCancelledAt);
        const isUnlisted = Boolean((item as any).isUnlisted);
        const isSellerBanned = Boolean((item as any).sellerBanned);
        return isOutOfStock || isUnavailable || hasNoShippingMethods || isCancelled || isUnlisted || isSellerBanned;
      });

      return { itemCount, uniqueItemCount, subtotal, shippingTotal, serviceFee, total, allShippingMethodsSelected, hasUnavailableItems };
    };

    const currencyMap: Record<string, CartItemData[]> = {};
    for (const item of cartItems) {
      const code = item.currency || DEFAULT_CURRENCY;
      (currencyMap[code] ||= []).push(item);
    }

    const currencies = Object.keys(currencyMap).sort();
    const byCurrency = Object.fromEntries(
      currencies.map(code => [code, { ...compute(currencyMap[code]), items: currencyMap[code] }])
    ) as Record<
      string,
      {
        items: CartItemData[];
        itemCount: number;
        uniqueItemCount: number;
        subtotal: number;
        shippingTotal: number;
        serviceFee: number;
        total: number;
        allShippingMethodsSelected: boolean;
        hasUnavailableItems: boolean;
      }
    >;

    // One checkout = one seller × one currency × one shipping mode. The third
    // axis is NOT cosmetic: `resolve-checkout-intent` fails a mixed-mode
    // intent server-side (`reason: "mixed_shipping_mode"`), and a seller may
    // legally have both `legacy` and `live_rate` listings in the same cart.
    // Grouping is where that mixture is made unrepresentable — see
    // lib/cart/checkout-groups.ts for the full rationale.
    const checkoutGroups = groupItemsForCheckout(cartItems);

    const bySeller = Object.fromEntries(
      checkoutGroups.map((group) => {
        const firstItem = group.items[0];
        const computed = compute(group.items);
        return [group.key, {
          ...computed,
          items: group.items,
          groupKey: group.key,
          sellerId: group.sellerId,
          sellerName: firstItem.sellerName || "Unknown Seller",
          sellerAvatarUrl: firstItem.sellerAvatarUrl || "",
          currencyCode: group.currencyCode,
          shippingMode: group.shippingMode,
          paypalMerchantId: firstItem.paypalMerchantId || null,
          paypalCapabilities: (firstItem as any).paypalCapabilities || [],
        } as SellerCalculation];
      })
    ) as Record<CheckoutGroupKey, SellerCalculation>;

    const global = compute(cartItems);

    return {
      ...global,
      currencies,
      byCurrency,
      bySeller,
      hasMultipleCurrencies: currencies.length > 1,
    };
  }, [cartItems, availability]);
}
